// #356: «More time» in the logger, the pure half. The athlete has 10, 20 or 30
// minutes more today: each model row says what it adds (ModelRow.moreTime),
// and this lists those steps in their order, with the seconds each one takes,
// while they fit. A superset grows by whole rounds. Only today's session
// grows: the program does not change. A slot without its model row
// (Slot.rowId) adds nothing, nor does a program without its origin. Keep free
// of React and any UI import.
import type { EffortScale } from "./effortScale.js";
import { modelRowEntry, type ModelMoreTime, type ModelRow, type ProgramModel } from "./programModel.js";
import { modelDayOfKeys, modelRowHas, slotKeysOf } from "./programModelBlock.js";
import { modelRowAt, modelRowExercise, ruleOnScale } from "./programModelDraft.js";
import { generateRounds, type RuleSet } from "./programProgression.js";
import { nextBlockMode } from "./programProgressionRounds.js";
import { pickActiveVariant } from "./programResolution.js";
import { EXERCISE_CHANGE_SEC, SIMULATION_SECONDS_PER_REP, workSeconds } from "./programSimulationSets.js";
import { restSecondsFor, SUPERSET_REST_SEC } from "./restTimer.js";
import type { Exercise } from "./schemas/exercise.js";
import type { Program, ProgramSession, SetPause, SetPrescription } from "./schemas/program.js";
import type { IntensityKind } from "./programEdit.js";

/** The minutes the sheet offers. */
export const MORE_TIME_MINUTES = [10, 20, 30] as const;
export type MoreTimeMinutes = (typeof MORE_TIME_MINUTES)[number];

export type MoreTimeItem = {
  rowId: string;
  add: ModelMoreTime["add"];
  exerciseId: string;
  /** The slot that grows; absent on a row that comes back. */
  slotId?: string;
  /** «set» in a superset: every member of the group gets a set, one round. */
  groupId?: string;
  /** «reps»: how many more on each set. */
  reps?: number;
  /** «row»: its sets this round, on `axis`, and the rest after each. */
  sets?: RuleSet[];
  axis?: IntensityKind;
  /** «row» on a timed rule: each set's `reps` is its seconds. */
  timed?: true;
  restSec?: number;
  /** «row»: the row's pause inside the rep (HI-8), on each set. */
  pause?: SetPause;
  seconds: number;
};

export type MoreTimeInput = {
  model: Pick<ProgramModel, "days" | "roundPattern">;
  program: Pick<Program, "origin" | "focus" | "roundTypes">;
  /** The day being logged and its round. */
  session: Pick<ProgramSession, "slots">;
  weekIdx: number;
  minutes: MoreTimeMinutes;
  effortScale: EffortScale;
  /** The catalog by slug: an exercise's own rest and how its sets are timed. */
  exercises?: ReadonlyMap<string, Exercise>;
  profileRestSec?: number | null;
  /** The sets still to do on each slot, by slot id, from the session being
   *  logged: «reps» grows only those. Absent: every set of the round. */
  setsToDo?: ReadonlyMap<string, number>;
  /** Slots whose «+1 set» and «+reps» have nowhere to land (no row in the
   *  draft, or a row in its MAX calibration), by slot id: they take no step,
   *  so they spend none of the minutes. A superset's round is not one. */
  skipSlots?: ReadonlySet<string>;
  /** The exercises already in the draft being logged: a «row» that comes back
   *  on one of them has nowhere to land, so it takes no step and no minutes. */
  inDraft?: ReadonlySet<string>;
};

type Step = { item: MoreTimeItem; pass: number; order: number; place: number };

type Run = ReadonlyArray<{ set: Pick<SetPrescription, "reps" | "work" | "restSec">; ex: Exercise | undefined }>;

/** Sets done back to back, one more set or one round of a superset: the work
 *  of each, a hop between members, and the rest after the last. */
const runSeconds = (run: Run, profileRestSec: number | null | undefined): number => {
  const end = run[run.length - 1];
  if (!end) return 0;
  const work = run.reduce((n, { set, ex }) => n + (workSeconds({ targetReps: set.reps, work: set.work }, ex) ?? 0), 0);
  const rest = restSecondsFor({ prescribedRestSec: end.set.restSec, supersetHop: false, exerciseRestSec: end.ex?.defaultRestSec, profileRestSec });
  return work + (run.length - 1) * SUPERSET_REST_SEC + rest;
};

/** The last set this round of each member of the group `gid`, in the day's order. */
const roundOf = (slots: ProgramSession["slots"], gid: string, weekIdx: number, input: MoreTimeInput): Run =>
  slots.flatMap((s) => {
    const sets = s.weeks[weekIdx]?.sets ?? [];
    const v = pickActiveVariant(s, weekIdx);
    return s.group?.id === gid && v && sets.length > 0 ? [{ set: sets[sets.length - 1]!, ex: input.exercises?.get(v.exerciseId) }] : [];
  });

/** The steps of every row, in the order they are taken, before the minutes cut them. */
const stepsOf = (input: MoreTimeInput): Step[] => {
  const { model, program, session, weekIdx, exercises, profileRestSec } = input;
  const origin = program.origin;
  if (!origin) return [];
  const slots = new Map(
    session.slots.flatMap((s) => {
      const sets = s.weeks[weekIdx]?.sets ?? [];
      const v = pickActiveVariant(s, weekIdx);
      return s.rowId && v && sets.length > 0 ? [[s.rowId, { slot: s, sets, exerciseId: v.exerciseId }] as const] : [];
    }),
  );
  if (slots.size === 0) return [];
  // The model's day is the one the slots were written from.
  const rows: ModelRow[] = model.days[modelDayOfKeys(model.days, slotKeysOf(session.slots))]?.rows ?? [];
  const types = model.roundPattern;
  const out: Step[] = [];
  const rounds = new Set<string>();
  rows.forEach((row, place) => {
    const more = row.moreTime;
    if (!more) return;
    const here = slots.get(row.id);
    const step = (pass: number, item: MoreTimeItem) => out.push({ item, pass, order: more.order, place });
    if (!here) {
      if (more.add !== "row") return;
      // Only a row the wizard left out for the minutes comes back: one it
      // wrote there and is gone, the athlete took out.
      if (modelRowAt(row, origin.level, origin.minutes, program.focus)) return;
      const at = modelRowEntry(row, origin.level, program.focus);
      if (!at) return;
      const exerciseId = modelRowExercise(at.entry, origin.picks[row.id]);
      if (input.inDraft?.has(exerciseId)) return;
      const rule = ruleOnScale(at.entry.rule, input.effortScale);
      // A rule that continues stands where the program's blocks took it
      // (addNextBlock); one that restarts or repeats is at the pattern's round
      // (a model's restart is a history row: no load to lift, #400).
      const upTo = Array.from({ length: weekIdx + 1 }, (_, w) =>
        program.roundTypes ? (program.roundTypes[w] ?? "normal") : (types[w % types.length] ?? "normal"),
      );
      const sets = nextBlockMode(rule) === "continue"
        ? (generateRounds(rule, upTo, upTo.length)[weekIdx] ?? [])
        : (generateRounds(rule, types, types.length)[weekIdx % types.length] ?? []);
      const ex = exercises?.get(exerciseId);
      const work = (s: RuleSet) => (rule.work === "countdown" ? { work: { kind: "countdown" as const, durationSec: s.reps } } : {});
      const one = (s: RuleSet) => runSeconds([{ set: { reps: s.reps, ...work(s), restSec: at.entry.restSec }, ex }], profileRestSec);
      // A new exercise: its sets, and one change of exercise to reach it.
      const seconds = EXERCISE_CHANGE_SEC + sets.reduce((sum, s) => sum + one(s), 0);
      const rest = {
        ...(at.entry.restSec !== undefined ? { restSec: at.entry.restSec } : {}),
        ...(at.entry.pause ? { pause: { ...at.entry.pause } } : {}),
      };
      const timed = rule.work === "countdown" ? { timed: true as const } : {};
      if (sets.length > 0) step(1, { rowId: row.id, add: "row", exerciseId, sets, axis: rule.axis, ...timed, ...rest, seconds });
      return;
    }
    // The athlete swapped the exercise for one the row does not have: the
    // row's sets and reps were made for the other, so no step on it.
    if (!modelRowHas(row, origin, here.exerciseId)) return;
    const exerciseId = here.exerciseId;
    const ex = exercises?.get(exerciseId);
    const last = here.sets[here.sets.length - 1]!;
    const base = { rowId: row.id, exerciseId, slotId: here.slot.id };
    const skip = input.skipSlots?.has(here.slot.id) === true;
    for (let pass = 1; pass <= (more.add === "row" ? 1 : (more.times ?? 1)); pass += 1) {
      if (more.add === "reps") {
        // Reps on a timed or AMRAP set have no meaning here.
        if (skip || here.sets.some((s) => s.work !== undefined || s.amrap === true)) return;
        const reps = more.reps ?? 0;
        const toDo = input.setsToDo?.get(here.slot.id) ?? here.sets.length;
        step(pass, { ...base, add: "reps", reps, seconds: reps * SIMULATION_SECONDS_PER_REP * toDo });
      } else if (here.slot.group) {
        // A superset keeps its rounds even: the whole group gets a round, once a pass.
        const gid = here.slot.group.id;
        // Any slot of the group skipped (calibration, absent, old draft): no round.
        if (session.slots.some((o) => o.group?.id === gid && input.skipSlots?.has(o.id) === true)) return;
        if (rounds.has(`${pass}:${gid}`)) continue;
        rounds.add(`${pass}:${gid}`);
        step(pass, { ...base, add: "set", groupId: gid, seconds: runSeconds(roundOf(session.slots, gid, weekIdx, input), profileRestSec) });
      } else {
        // «row» already there: a set on it.
        if (skip) return;
        step(pass, { ...base, add: "set", seconds: runSeconds([{ set: last, ex }], profileRestSec) });
      }
    }
  });
  return out.sort((a, b) => a.pass - b.pass || a.order - b.order || a.place - b.place);
};

/** What today's session adds with `minutes` more, in order: each step that
 *  still fits in what is left of them. Empty without the model rows. */
export const moreTimeItems = (input: MoreTimeInput): MoreTimeItem[] => {
  let left = input.minutes * 60;
  const out: MoreTimeItem[] = [];
  for (const { item } of stepsOf(input)) {
    if (item.seconds <= 0 || item.seconds > left) continue;
    left -= item.seconds;
    out.push(item);
  }
  return out;
};
